import { useCallback, useMemo } from 'react';
import numeral from 'numeral';
import number, { parseCountAbbrv2 } from 'utils/number';
import {
  formatAverage,
  formatChangePrevious,
  formatNumberWithNA,
  formatPercentageChange,
  getShowIepIev,
} from 'features/company/Stream/TableOrderbook/utils';
import { useLivePriceSingleReceiver } from 'global/Datafeed/UseLivePrice/useLivePriceSingleReceiver';
import TableOHLNonStock from 'features/company/Stream/TableOrderbook/TableOHLNonStock';
import TableOHLStock from 'features/company/Stream/TableOrderbook/TableOHLStock';
import { setValue, useOrderbookStore as wsStore } from './store';

/* eslint-disable no-undef */
interface TableLivePriceProps {
  // orderbook: Partial<CompanyOrderbook>;
  orderbook: any;
  symbol: string;
  isStock: boolean;
}

function TableLivePrice({ orderbook, symbol, isStock }: TableLivePriceProps) {
  const livePrice = wsStore(({ liveprice }) => liveprice?.[symbol]);
  const data = livePrice || orderbook;

  const previous = useMemo(
    () => formatChangePrevious(data.previous),
    [data.previous],
  );
  const open = useMemo(() => formatNumberWithNA(data.open), [data.open]);
  const lot = useMemo(
    () =>
      data.volume
        ? number.parseCountAbbrv3(number.parseLot(data.volume), 1, 4)
        : 'NA',
    [data.volume],
  );
  const change = useMemo(
    () => formatChangePrevious(data.change),
    [data.change],
  );
  const newChange = useMemo(
    () => (change === 'NA' ? change : numeral(change).value()),
    [change],
  );
  const formattedChange = useMemo(
    () => number.addPlusOrMinus2(newChange),
    [newChange],
  );
  const high = useMemo(() => formatNumberWithNA(data.high), [data.high]);
  const value = useMemo(
    () => (data.value ? parseCountAbbrv2(data.value, 1) : 'NA'),
    [data.value],
  );
  const percentageChange = useMemo(
    () => formatPercentageChange(data.percentage_change),
    [data.percentage_change],
  );
  const average = useMemo(() => formatAverage(data.average), [data.average]);
  const low = useMemo(() => formatNumberWithNA(data.low), [data.low]);
  const frequency = useMemo(
    () => (data.frequency ? numeral(data.frequency).format('0,0') : ''),
    [data.frequency],
  );
  const fbuy = useMemo(
    () =>
      data.fbuy ? numeral(data.fbuy).format('0.0 a')?.toUpperCase() : '',
    [data.fbuy],
  );
  const fsell = useMemo(
    () =>
      data.fsell ? numeral(data.fsell).format('0.0 a')?.toUpperCase() : '',
    [data.fsell],
  );

  const [Iep, Iev, showIepIev] = useMemo(() => {
    if (!data.iepiev?.iep && !data.iepiev?.iev) return [];
    const iep = formatNumberWithNA(data.iepiev.iep.raw, '-');
    const iev = data.iepiev.iev.raw
      ? parseCountAbbrv2(data.iepiev.iev.raw, 1, ',', true)
      : '-';
    const timeleft = data.iepiev.time_left_seconds;
    const showiepiev = getShowIepIev(!!(iep && iev), timeleft);
    return [iep, iev, showiepiev];
  }, [data.iepiev]);

  const fourthRowExist = useMemo(
    () => showIepIev || data.is_foreignbs_exist,
    [showIepIev, data.is_foreignbs_exist],
  );

  const isTwoColumn = useMemo(
    () => !data.exchange && Boolean(data.symbol),
    [data.exchange, data.symbol],
  );

  const ara = useMemo(() => {
    if (orderbook.ara.visible) {
      return orderbook?.ara?.value;
    }
    return '-';
  }, [orderbook.ara]);

  const arb = useMemo(() => {
    if (orderbook.arb.visible) {
      return orderbook?.arb?.value;
    }
    return '-';
  }, [orderbook.arb]);

  const livePriceOnMessage = useCallback((message) => {
    setValue({
      key: 'liveprice',
      symbol: message.stockCode,
      value: { ...message, symbol: message.stockCode },
    });
  }, []);

  useLivePriceSingleReceiver(
    symbol,
    'sidebar_orderbook_summary',
    livePriceOnMessage,
  );

  if (isStock) {
    return (
      <TableOHLStock
        data={{
          average,
          high,
          change: formattedChange,
          lot,
          low,
          open,
          percentage: percentageChange,
          previous,
          value,
          ara,
          arb,
          fbuy,
          frequency,
          fsell,
          Iep,
          Iev,
        }}
        isTwoColumn={isTwoColumn}
        showForthRow={fourthRowExist}
        showIepIev={showIepIev}
      />

    );
  }

  return (
    <TableOHLNonStock
      data={{
        average,
        high,
        change: formattedChange,
        lot,
        low,
        open,
        percentage: percentageChange,
        previous,
        value,
      }}
      isTwoColumn={isTwoColumn}
    />
  );
}

export default TableLivePrice;
